import React, { useState, useContext } from 'react';
import ReactDOM from 'react-dom';
import FighterSelectorView from './FighterSelectorView';
import './FighterSelector.css';
import { BASE_URL, TELLURIAN_FIGHTER, CELESTIAL_FIGHTER, MACHINA_FIGHTER } from '../../../../config';
import UserContext from '../../../../UserContext';
import TraitFilterBar from '../../../TraitFilterBar';

const FACTIONS = [
    { key: 'celestial', label: 'Celestial Fighters', contract: CELESTIAL_FIGHTER },
    { key: 'machine', label: 'Machine Fighters', contract: MACHINA_FIGHTER },
    { key: 'tellurian', label: 'Tellurian Fighters', contract: TELLURIAN_FIGHTER },
];

function sortMountedFirst(nfts) {
    return [...nfts].sort((a, b) => {
        if (a.is_mounted === b.is_mounted) return 0;
        return a.is_mounted ? -1 : 1;
    });
}

function getButtonModes(nfts, globalMounted) {
    if (globalMounted) {
        const mountedNft = nfts.find(nft => nft.is_mounted);
        const mountedId = mountedNft ? mountedNft.token_id : null;
        return nfts.reduce((acc, nft) => {
            if (nft.token_id === mountedId) acc[nft.token_id] = "dismount";
            else acc[nft.token_id] = "swap";
            return acc;
        }, {});
    } else {
        // None mounted globally, so all "mount"
        return nfts.reduce((acc, nft) => {
            acc[nft.token_id] = "mount";
            return acc;
        }, {});
    }
}

const FighterFactionSelector = ({ onClose, onMountChange }) => {
    const [selectedFaction, setSelectedFaction] = useState(null);
    const [nfts, setNfts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedNFT, setSelectedNFT] = useState(null);
    const { userInfo } = useContext(UserContext) || {};
    const [filteredNfts, setFilteredNfts] = useState([]);
    const [resetKey, setResetKey] = useState(0);
    const [globalMounted, setGlobalMounted] = useState(false);   
    const buttonModes = getButtonModes(filteredNfts, globalMounted);

    // Helper to fetch user's NFTs by faction
    const fetchNfts = async (factionKey) => {
        setLoading(true);
        setNfts([]);
        setFilteredNfts([]);
        setSelectedNFT(null);

        setResetKey(prev => prev + 1);
        setSelectedFaction(factionKey);

        if (!userInfo || !userInfo.wallet_address || !userInfo.jwt) {
            setLoading(false);
            setNfts([]);
            return;
        }

        try {
            const faction = FACTIONS.find(f => f.key === factionKey);
            const res = await fetch(`${BASE_URL}/get_user_nfts`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    address: userInfo.wallet_address,
                    jwt: userInfo.jwt,
                    nft_contract: faction.contract,
                }),
            });
            const data = await res.json();
            setGlobalMounted(!!data.global_mounted);
            const adaptedNfts = (data.nfts || []).map(nft => ({
                ...nft,
                traits: nft.metadata?.attributes || []
            }));

            //Do NOT filter by .collection since backend does it for you!
            setNfts(sortMountedFirst(adaptedNfts));
            setFilteredNfts(sortMountedFirst(adaptedNfts));
            console.log('API /get_user_nfts response:', data);
        } catch (err) {
            setNfts([]);
            setFilteredNfts([]);
            console.error('Failed to fetch NFTs:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (activeFilters, sortOption, idSearch) => {
        let toShow = nfts;

        // 1. Filter by traits
        if (Object.keys(activeFilters).length) {
            toShow = toShow.filter(nft =>
                Object.entries(activeFilters).every(([traitType, values]) =>
                    values.some(
                        value =>
                            nft.traits &&
                            nft.traits.some(
                                trait => trait.trait_type === traitType && trait.value === value
                            )
                    )
                )
            );
        }

        // 2. Filter by NFT ID search (as substring)
        if (idSearch && idSearch.trim() !== '') {
            toShow = toShow.filter(nft =>
                String(nft.token_id).includes(idSearch)
            );
        }

        // 3. Sort by selected sortOption
        if (sortOption === "id") {
            toShow = [...toShow].sort((a, b) => Number(a.token_id) - Number(b.token_id));
        }
        // Always ensure mounted is first!
        setFilteredNfts(sortMountedFirst(toShow));
    };

    const modal = (
        <div className="faction-selector-overlay">
            <div className="faction-selector-popup">
                <button className="faction-selector-close" onClick={onClose}>{'\u00D7'}</button>
                <div className="faction-selector-header">
                    {FACTIONS.map(faction => (
                        <button
                            key={faction.key}
                            className={`faction-tab${selectedFaction === faction.key ? ' active' : ''}`}
                            onClick={() => fetchNfts(faction.key)}
                        >
                            {faction.label}
                        </button>
                    ))}
                </div>
                <TraitFilterBar
                    nfts={nfts}
                    onFilterChange={handleFilterChange}
                    resetKey={resetKey}
                    enablePriceFilter={false}
                />
                <div className="faction-nft-list">
                    {loading && <div className="faction-nft-loading">Loading NFTs...</div>}
                    {!loading && filteredNfts.map((nft, idx) => (
                        <div
                            className={`faction-nft-item${nft.is_mounted ? ' mounted-nft' : ''}`}
                            key={nft.token_id || idx}
                            onClick={() => setSelectedNFT(nft)}
                            style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}
                        >
                            <span className="faction-nft-id" style={{
                                minWidth: 56,
                                fontWeight: 700,
                                color: '#6193f7',
                                fontFamily: "'Orbitron', 'Segoe UI', Arial, sans-serif",
                                marginRight: 18,
                                fontSize: '1.1em'
                            }}>
                                {nft.token_id}
                            </span>
                            <div className="faction-nft-traitgrid">
                                {nft.metadata?.attributes?.map((attr, j) => (
                                    <div className="faction-nft-trait" key={j}>
                                        <span className="faction-nft-trait-type">{attr.trait_type}:</span>
                                        <span className="faction-nft-trait-value">{attr.value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(
        <>
            {modal}
            {selectedNFT && (
                <FighterSelectorView
                    nft={selectedNFT}
                    contractAddress={selectedFaction ? FACTIONS.find(f => f.key === selectedFaction)?.contract : undefined}
                    userInfo={userInfo}
                    onClose={() => setSelectedNFT(null)}
                    buttonMode={buttonModes[selectedNFT?.token_id] || "mount"}
                    onMountChange={onMountChange} // <-- NEW!
                />
            )}
        </>,
        document.body
    );
};

export default FighterFactionSelector;
